'use strict';

/**
 * shared/graph/confidence.js — THE ONE canonical relationship-edge confidence scorer.
 *
 * WHY THIS EXISTS (consolidation U11)
 *   `confidenceFromSources` / `effectiveConfidence` were hand-mirrored across two runtimes:
 *   the backend MCP read path (`backend/src/mcp/lib/relationship-graph.mjs`) and the
 *   frontend type layer (`src/types/relationships.ts`). Two copies, drift guaranteed —
 *   the read path is the live entity-graph moat, so a silent divergence would show
 *   different confidence badges to agents vs. the site. This module is the single source
 *   both runtimes consume, exactly like `shared/publish/transitions.js` and
 *   `shared/config/monitored-collections.js` (plain CJS, ZERO requires, importable by the
 *   Node/ESM backend AND Next.js TS via the `@shared/*` alias + a `.d.ts` sidecar).
 *
 * PURITY CONTRACT
 *   Requires NOTHING (no firebase, no env, no clock). Pure functions of their inputs —
 *   referentially transparent, trivially importable from a browser bundle, and safe to
 *   classify as a pure test target.
 *
 * ANTI-CIRCULAR-CITATION FIREWALL (do not weaken — the moat depends on it)
 *   Self-sourced evidence (`independent === false` OR `sourceType === 'self'`) is excluded
 *   from BOTH the human-verified tier and reported corroboration. Cryptographic proofs
 *   (on-chain / signed-message) are self-evidencing and exempt — the math, not the
 *   asserter, establishes them. Corroboration counts DISTINCT OUTLETS (url host) so the
 *   same article relabelled twice cannot fake it.
 */

/** Per-source strength: verified(3) > reported(2) > inferred(1). */
function sourceStrength(s) {
  const vm = s?.verificationMethod;
  if (vm === 'signed-message' || vm === 'onchain' || vm === 'human-confirmed') return 3;
  if (vm === 'official-bio' || vm === 'news') return 2;
  return 1;
}

/** A source counts toward INDEPENDENT corroboration only if not self-flagged and not sourceType 'self'. */
function isIndependentSource(s) {
  return s?.independent !== false && s?.sourceType !== 'self';
}

/** Hostname of a source URL (distinct-outlet corroboration); null if absent/unparseable. 'www.' stripped
 *  so www.nyt.com and nyt.com count as ONE outlet (can't fake corroboration with a www variant). */
function hostOf(url) {
  if (!url) return null;
  try { return new URL(url).host.toLowerCase().replace(/^www\./, ''); } catch (_) { return null; }
}

/**
 * Confidence COMPUTED from the corroboration array:
 *  - a self-evidencing proof (on-chain / signed-message) OR an INDEPENDENT human-confirmation → high
 *  - ≥2 INDEPENDENT reported sources from DISTINCT outlets (url host, else sourceType) → high
 *  - one independent reported source → medium
 *  - else (agent-only / self-sourced / none) → low
 */
function confidenceFromSources(sources = []) {
  if (!sources.length) return 'low';
  const verified = sources.some(s => {
    const vm = s?.verificationMethod;
    if (vm === 'onchain' || vm === 'signed-message') return true;   // self-evidencing (cryptographic)
    if (vm === 'human-confirmed') return isIndependentSource(s);    // a self-confirmation is not ground truth
    return false;
  });
  if (verified) return 'high';
  const reported = sources.filter(s => sourceStrength(s) === 2 && isIndependentSource(s));
  const distinctOutlets = new Set(reported.map(s => hostOf(s.url) || s.sourceType)).size;
  if (distinctOutlets >= 2) return 'high';
  if (reported.length >= 1) return 'medium';
  return 'low';
}

/** The single confidence shown: explicit human override, else computed from sources. */
function effectiveConfidence(edge) {
  return edge.confidence ?? confidenceFromSources(edge.sources);
}

module.exports = {
  confidenceFromSources,
  effectiveConfidence,
};
